import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './styles/globals.css';
import { routeTree } from './routeTree.gen';
import { initI18n } from './lib/i18n';
import { ApiError } from './lib/api';
import { track } from './lib/tracker';

// Error reporting: Sentry only when a DSN is configured at build time (VITE_SENTRY_DSN); uncaught errors always land in the activity log.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (sentryDsn) {
  void import('@sentry/react').then((S) => S.init({ dsn: sentryDsn, environment: import.meta.env.MODE, tracesSampleRate: 0.1, sendDefaultPii: false }));
}
window.addEventListener('error', (e) => track('client.error', { meta: { message: String(e.message ?? '').slice(0, 300), source: (e.filename ?? '').slice(0, 200), line: e.lineno } }));
window.addEventListener('unhandledrejection', (e) => track('client.error', { meta: { message: String((e.reason as Error | undefined)?.message ?? e.reason ?? '').slice(0, 300), kind: 'unhandledrejection' } }));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, err) => !(err instanceof ApiError && err.status > 0 && err.status < 500) && count < 2,
      staleTime: 15_000,
      refetchOnWindowFocus: false,
    },
  },
});

const router = createRouter({ routeTree, context: { queryClient }, defaultPreload: 'intent', scrollRestoration: true });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

async function boot() {
  await initI18n();
  const root = document.getElementById('root')!;
  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </React.StrictMode>,
  );
}

void boot();
